import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import SignatureCanvas from 'react-signature-canvas'
import { Button } from 'components/UIElements'
import moment from 'moment'
import { CONSENT_TEXT, CONSENT_SIGNATURE_MODES, CONSENT_ALT_TEXT, LOCAL_STORAGE_KEYS, COLLAPSED, EXPANDED } from 'utils/constants'
import { close as closeSvg } from '../../../assets/assets'
import cameraIcon from '../../../assets/camera.svg'
import unionIcon from '../../../assets/union.svg'
import resizeMaxIcon from '../../../assets/resizemax.svg'
import resizeMinIcon from '../../../assets/resizemin.svg'
import SignatureUpload from './SignatureUpload'

// Shared canvas utilities to reduce duplication and ensure consistency
const getCanvasFromRef = sigRef => {
  return sigRef?.current?.getCanvas ? sigRef.current.getCanvas() : sigRef?.current?._canvas
}

const getDevicePixelRatioSafe = () => {
  try {
    return (typeof window !== 'undefined' && window.devicePixelRatio) ? window.devicePixelRatio : 1
  } catch (_) {
    return 1
  }
}

const getLogicalCanvasSize = canvas => {
  const dpr = getDevicePixelRatioSafe()
  return {
    width: (canvas?.width || 0) / dpr,
    height: (canvas?.height || 0) / dpr
  }
}

const ensureCanvasSized = (canvas, logicalW, logicalH, applyHidpiTransform = true) => {
  if (!canvas) return null
  const dpr = getDevicePixelRatioSafe()
  if (canvas.style) {
    canvas.style.width = `${logicalW}px`
    canvas.style.height = `${logicalH}px`
  }
  const targetW = Math.round(logicalW * dpr)
  const targetH = Math.round(logicalH * dpr)
  if (canvas.width !== targetW || canvas.height !== targetH) {
    canvas.width = targetW
    canvas.height = targetH
  }
  const ctx = canvas.getContext('2d')
  if (ctx && applyHidpiTransform) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.imageSmoothingEnabled = true
    // @ts-ignore legacy engines
    if (ctx.msImageSmoothingEnabled !== undefined) {
      ctx.msImageSmoothingEnabled = true
    }
    if ('imageSmoothingQuality' in ctx) {
      ctx.imageSmoothingQuality = 'high'
    }
  }
  return ctx
}

const isEmpty = ref => {
  if (!ref) return true
  return !!ref.isEmpty()
}

const generateSigData = ref => {
  const data = ref?.toDataURL()
  return data.split(',')[1]
}

const SigCanvas = SignatureCanvas.default || SignatureCanvas

const Signature = props => {
  const sigRef = useRef(null)
  const signingAreaRef = useRef(null)
  const expandPreferenceRef = useRef('auto') // 'auto' | 'expanded' | 'collapsed'
  const persistedNoticeTimeoutRef = useRef(null)
  const [sigHeight, setSigHeight] = useState(256)
  const [sigWidth, setSigWidth] = useState(512)
  const [signed, setSigned] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [savedSigData, setSavedSigData] = useState([])
  const [savedSigImage, setSavedSigImage] = useState('')
  const prevCanvasSizeRef = useRef({ width: 0, height: 0 })
  const originalSignatureRef = useRef({ data: null, image: null, canvasSize: null })
  const [showPersistedNotice, setShowPersistedNotice] = useState(false)
  const [forceImageRestore, setForceImageRestore] = useState(false)
  const {
    fullName,
    isFinalSection,
    onSignUpdate = () => { },
    strings,
    signature,
    readOnly,
    defaultSignaturePadRatio,
    promptText,
    canSkip,
    questionId,
    openModal,
    skipConsentQuestion,
    signatureOptions = ['draw'],
    showToggle = true,
    consentName,
    consentVersion,
    pdfUrl,
    printToSign,
    consentId,
    dispatch,
    isCaregiverSignature = false
  } = props

  const signaturePadRatio = defaultSignaturePadRatio || 0.5
  const expandedSignaturePadRatio = 0.8
  const [mode, setMode] = useState(() => {
    if (Array.isArray(signatureOptions)) {
      // If only one option is available, use that
      if (signatureOptions.length === 1) {
        return signatureOptions[0]
      }
      // If both options available, prefer photo_upload
      if (signatureOptions.includes(CONSENT_SIGNATURE_MODES.photoUpload)) {
        return CONSENT_SIGNATURE_MODES.photoUpload
      }
      return CONSENT_SIGNATURE_MODES.draw
    }
    return CONSENT_SIGNATURE_MODES.draw
  })
  const [uploadedFile, setUploadedFile] = useState({})
  const [isUploading, setIsUploading] = useState(false)
  const resizeRafRef = useRef(0)

  // Consolidated condition used across UI to lock switching once any signature exists
  const isSignatureLocked = signed || !!uploadedFile?.name

  // Auto-expand on mobile landscape for better signing area and preserve drawing
  useEffect(() => {
    const maybeAutoExpand = () => {
      try {
        if (typeof window === 'undefined') return
        const isLandscape = window.matchMedia && window.matchMedia('(orientation: landscape)').matches
        const vw = window.innerWidth || 0
        const vh = window.innerHeight || 0
        // Treat as mobile if narrowest dimension <= 768
        const isMobileLike = Math.min(vw, vh) <= 768
        // Only auto-expand if the user hasn't explicitly chosen a preference
        if (isLandscape && isMobileLike && !isExpanded && expandPreferenceRef.current === 'auto') {
          // If there is a signature, snapshot it before expanding so it restores cleanly
          if (sigRef?.current && !sigRef?.current?.isEmpty()) {
            try {
              const canvas = getCanvasFromRef(sigRef)
              if (canvas) {
                const size = getLogicalCanvasSize(canvas)
                prevCanvasSizeRef.current = { width: size.width, height: size.height }
              } else {
                prevCanvasSizeRef.current = { width: sigWidth, height: sigHeight }
              }
              captureSignatureSnapshot()
            } catch (_) {
              // no-op; fallback handled by resize effect
            }
          }
          setIsExpanded(true)
          expandPreferenceRef.current = 'expanded'
        }
      } catch (_) {
        // no-op
      }
    }
    // Initial check
    maybeAutoExpand()
    // Listen for changes
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', maybeAutoExpand)
      window.addEventListener('orientationchange', maybeAutoExpand)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', maybeAutoExpand)
        window.removeEventListener('orientationchange', maybeAutoExpand)
      }
    }
  }, [isExpanded, sigWidth, sigHeight])

  useEffect(() => {
    if (signature && readOnly) {
      if (signature.includes('data:image/png;base64,')) {
        // eslint-disable-next-line no-unused-expressions
        sigRef?.current?.fromDataURL(signature)
      } else {
        // eslint-disable-next-line no-unused-expressions
        sigRef?.current?.fromDataURL(`data:image/png;base64,${signature}`)
      }
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const handleResize = () => {
      // Coalesce rapid resize events into a single frame
      if (resizeRafRef.current) return
      resizeRafRef.current = (typeof window !== 'undefined' && window.requestAnimationFrame)
        ? window.requestAnimationFrame(() => {
          if (!isMounted) {
            resizeRafRef.current = 0
            return
          }
          resizeRafRef.current = 0
          const width = signingAreaRef?.current?.clientWidth
          if (!width) return

          // Preserve current signature across resize if any
          // IMPORTANT: Only save the ORIGINAL signature once, not the scaled version
          try {
            const hasSignature = sigRef?.current && !sigRef?.current?.isEmpty()
            if (hasSignature) {
              if (!originalSignatureRef.current.data && !originalSignatureRef.current.image) {
                const data = sigRef?.current?.toData() || []
                const img = sigRef?.current?.toDataURL() || ''
                const canvas = getCanvasFromRef(sigRef)
                const originalCanvasSize = canvas
                  ? getLogicalCanvasSize(canvas)
                  : { width: sigWidth, height: sigHeight }

                if (data.length > 0 || img) {
                  originalSignatureRef.current = {
                    data,
                    image: img,
                    canvasSize: originalCanvasSize
                  }
                  if (isMounted) {
                    setSavedSigData(data)
                    if (img) setSavedSigImage(img)
                    setForceImageRestore(!(Array.isArray(data) && data.length))
                  }
                }
                prevCanvasSizeRef.current = originalCanvasSize
              } else {
                const canvas = getCanvasFromRef(sigRef)
                if (canvas) {
                  const size = getLogicalCanvasSize(canvas)
                  prevCanvasSizeRef.current = { width: size.width, height: size.height }
                } else {
                  prevCanvasSizeRef.current = { width: sigWidth, height: sigHeight }
                }
                if (isMounted) {
                  setSavedSigData(originalSignatureRef.current.data || [])
                  if (originalSignatureRef.current.image) {
                    setSavedSigImage(originalSignatureRef.current.image)
                  }
                  setForceImageRestore(!(Array.isArray(originalSignatureRef.current.data) && originalSignatureRef.current.data.length))
                }
              }
            } else {
              if (!savedSigData?.length && !savedSigImage) {
                if (isMounted) {
                  setSavedSigData([])
                  setSavedSigImage('')
                  setForceImageRestore(false)
                }
                originalSignatureRef.current = { data: null, image: null, canvasSize: null }
              }
            }
          } catch (err) {
            console.error('Error saving signature during resize:', err)
            prevCanvasSizeRef.current = { width: sigWidth, height: sigHeight }
          }

          // Update canvas dimensions
          if (isMounted) {
            setSigWidth(width)
            const ratio = isExpanded ? expandedSignaturePadRatio : signaturePadRatio
            setSigHeight(ratio * width)
          }
        })
        : 0
    }

    window.addEventListener('resize', handleResize)

    handleResize()

    return () => {
      isMounted = false
      window.removeEventListener('resize', handleResize)
      // Cancel any pending RAF
      if (resizeRafRef.current && typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(resizeRafRef.current)
        resizeRafRef.current = 0
      }
    }
    // Depend only on signaturePadRatio so we don't rebind the resize listener on every render
    // which can cause jank if parent passes a new onSignUpdate each render.
  }, [signaturePadRatio, isExpanded])

  // Ensure crisp rendering on HiDPI/zoomed displays by scaling canvas backing store
  useEffect(() => {
    try {
      const canvas = getCanvasFromRef(sigRef)
      if (!canvas) return
      const cssW = sigWidth || 512
      const cssH = sigHeight || 256
      ensureCanvasSized(canvas, cssW, cssH, true)
    } catch (err) {
      console.error('HiDPI scaling error:', err)
    }
    // Run whenever canvas CSS dimensions change
  }, [sigWidth, sigHeight, isExpanded])

  // Restore after size changes
  useEffect(() => {
    let isMounted = true
    const rafIds = []
    const timeoutIds = []

    if (forceImageRestore && savedSigImage) {
      let attempts = 0
      const maxAttempts = 30 // Prevent infinite loops
      const restoreImg = () => {
        if (!isMounted) return
        try {
          const canvas = getCanvasFromRef(sigRef)
          if (!canvas) {
            if (isMounted) setForceImageRestore(false)
            return
          }
          // Get target dimensions from state
          const targetW = sigWidth || 512
          const targetH = sigHeight || 256

          // Ensure canvas dimensions match our state
          // React-signature-canvas should update via props, but we'll ensure it's correct
          const dpr = getDevicePixelRatioSafe()
          const currentW = (canvas.width / dpr)
          const currentH = (canvas.height / dpr)

          // Ensure canvas dimensions match target - react-signature-canvas should update via props
          // But we need to wait for it or force it if needed
          if (currentW !== targetW || currentH !== targetH) {
            attempts++
            if (attempts < maxAttempts) {
              // Canvas hasn't resized yet, try again
              if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                const rafId = window.requestAnimationFrame(restoreImg)
                rafIds.push(rafId)
              } else {
                const timeoutId = setTimeout(restoreImg, 16) // ~60fps
                timeoutIds.push(timeoutId)
              }
              return
            }
            // If canvas still hasn't resized after max attempts, force it
            // This ensures the canvas is the correct size for proper scaling
            // Note: Directly setting canvas.width/height will clear the canvas, which is fine since we're restoring
            ensureCanvasSized(canvas, targetW, targetH, true)
          }

          const ctx = canvas.getContext('2d')
          if (!ctx) {
            if (isMounted) setForceImageRestore(false)
            return
          }

          // Clear canvas and reset transform
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
          ctx.clearRect(0, 0, targetW, targetH)

          // Use high-quality image smoothing for scaling to prevent blur
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          // @ts-ignore legacy engines
          if (ctx.msImageSmoothingEnabled !== undefined) {
            ctx.msImageSmoothingEnabled = true
          }

          const img = new Image()
          img.onload = () => {
            if (!isMounted) return
            try {
              // ALWAYS scale from the TRUE original canvas size to prevent cumulative scaling
              // This is the size when the signature was first drawn
              const originalSize = originalSignatureRef.current.canvasSize || {}
              const originalW = originalSize.width
              const originalH = originalSize.height

              // Use image natural dimensions as fallback if original size not available
              // but prefer the stored original canvas size
              const sourceW = originalW || img.naturalWidth || img.width
              const sourceH = originalH || img.naturalHeight || img.height

              if (!sourceW || !sourceH || sourceW === 0 || sourceH === 0) {
                console.error('Invalid image dimensions:', { sourceW, sourceH, naturalW: img.naturalWidth, naturalH: img.naturalHeight, originalSize })
                if (isMounted) setForceImageRestore(false)
                return
              }

              // Calculate scale factors from TRUE original canvas size to current canvas size
              // This ensures the signature always scales from the original, preventing cumulative scaling
              const scaleX = targetW / sourceW
              const scaleY = targetH / sourceH

              // Use the minimum scale to ensure the entire signature fits without cropping
              // This maintains aspect ratio while scaling according to canvas size
              const uniformScale = Math.min(scaleX, scaleY)
              const scaledW = sourceW * uniformScale
              const scaledH = sourceH * uniformScale

              // Verify scaled dimensions don't exceed canvas (shouldn't with uniformScale, but double-check)
              if (scaledW > targetW || scaledH > targetH) {
                // Recalculate with stricter bounds
                const safeScale = Math.min(targetW / sourceW, targetH / sourceH)
                const safeW = sourceW * safeScale
                const safeH = sourceH * safeScale

                // Center the scaled signature on the canvas
                const offsetX = (targetW - safeW) / 2
                const offsetY = (targetH - safeH) / 2

                // Draw the image scaled proportionally to fit within the canvas without cropping
                ctx.drawImage(img, 0, 0, sourceW, sourceH, offsetX, offsetY, safeW, safeH)
              } else {
                // Center the scaled signature on the canvas
                const offsetX = (targetW - scaledW) / 2
                const offsetY = (targetH - scaledH) / 2

                // Draw the image scaled proportionally to fit within the canvas without cropping
                ctx.drawImage(img, 0, 0, sourceW, sourceH, offsetX, offsetY, scaledW, scaledH)
              }

              if (isMounted) {
                setSigned(true)
                setForceImageRestore(false)
              }
            } catch (err) {
              console.error('Error restoring signature image:', err)
              if (isMounted) setForceImageRestore(false)
            }
          }
          img.onerror = () => {
            console.error('Error loading signature image')
            if (isMounted) setForceImageRestore(false)
          }
          img.src = savedSigImage
        } catch (err) {
          console.error('Error in restoreImg:', err)
          if (isMounted) setForceImageRestore(false)
        }
      }
      // Wait for canvas to resize - use multiple RAF calls to ensure dimensions are updated
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        const rafId1 = window.requestAnimationFrame(() => {
          if (!isMounted) return
          const rafId2 = window.requestAnimationFrame(() => {
            if (!isMounted) return
            const rafId3 = window.requestAnimationFrame(restoreImg)
            rafIds.push(rafId3)
          })
          rafIds.push(rafId2)
        })
        rafIds.push(rafId1)
      } else {
        const timeoutId = setTimeout(restoreImg, 100)
        timeoutIds.push(timeoutId)
      }
      return
    }
    if (savedSigData && savedSigData.length) {
      // Use a function to ensure canvas is properly sized before restoration
      const restoreVectorData = () => {
        if (!isMounted) return
        try {
          const canvas = getCanvasFromRef(sigRef)
          if (!canvas) {
            return
          }

          // Ensure canvas is properly sized to match state
          const targetW = sigWidth || 512
          const targetH = sigHeight || 256

          const dpr = getDevicePixelRatioSafe()
          ensureCanvasSized(canvas, targetW, targetH, true)

          // ALWAYS use the TRUE original canvas size for scaling calculations to prevent cumulative scaling
          // Never use prevCanvasSizeRef as it gets updated on each resize and would cause cumulative scaling
          const originalSize = originalSignatureRef.current.canvasSize || {}
          const { width: originalW, height: originalH } = originalSize
          const newW = targetW
          const newH = targetH
          // Only scale if we have the true original size and it's different from current size
          const shouldScale = originalW && originalH && (originalW !== newW || originalH !== newH)

          const scaleData = (data, sx, sy) => {
            try {
              return data.map(stroke => ({
                ...stroke,
                points: stroke.points.map(p => ({ ...p, x: p.x * sx, y: p.y * sy }))
              }))
            } catch (_) {
              return data
            }
          }

          // Use uniform scaling to maintain aspect ratio and prevent cropping
          // Always scale from original canvas size to prevent cumulative scaling
          let scaleX = 1
          let scaleY = 1
          if (shouldScale && originalW && originalH) {
            scaleX = newW / originalW
            scaleY = newH / originalH
            // Use minimum scale to ensure signature fits without cropping
            const uniformScale = Math.min(scaleX, scaleY)
            scaleX = uniformScale
            scaleY = uniformScale
          }

          // If we need to scale, use image restoration instead of vector data
          // Image scaling is more reliable than scaling vector coordinates
          if (shouldScale && savedSigImage) {
            // Use image restoration path
            const img = new Image()
            img.onload = () => {
              if (!isMounted) return
              try {
                const ctx = canvas.getContext('2d')
                if (!ctx) return

                // Clear canvas with HiDPI transform
                const dpr = getDevicePixelRatioSafe()
                const logicalW = (canvas.width / dpr)
                const logicalH = (canvas.height / dpr)
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
                ctx.clearRect(0, 0, logicalW, logicalH)

                // Get source dimensions
                const sourceW = img.naturalWidth || img.width
                const sourceH = img.naturalHeight || img.height

                // Always scale from original canvas size to prevent cumulative scaling
                const originalSize = originalSignatureRef.current.canvasSize || { width: sourceW, height: sourceH }
                const originalW = originalSize.width || sourceW
                const originalH = originalSize.height || sourceH

                // Calculate scale factors from original to current canvas (use logical CSS size)
                const imgScaleX = logicalW / originalW
                const imgScaleY = logicalH / originalH
                const uniformImgScale = Math.min(imgScaleX, imgScaleY)
                const scaledW = originalW * uniformImgScale
                const scaledH = originalH * uniformImgScale

                // Determine if we're scaling up or down
                const isScalingUp = uniformImgScale > 1

                // For scaling up, disable smoothing to keep sharp edges (pixelated but sharp)
                // For scaling down, use high-quality smoothing to prevent blur
                if (isScalingUp) {
                  ctx.imageSmoothingEnabled = false
                  if (ctx.msImageSmoothingEnabled !== undefined) {
                    ctx.msImageSmoothingEnabled = false
                  }
                } else {
                  ctx.imageSmoothingEnabled = true
                  ctx.imageSmoothingQuality = 'high'
                  if (ctx.msImageSmoothingEnabled !== undefined) {
                    ctx.msImageSmoothingEnabled = true
                  }
                }

                // Center the scaled image using logical canvas size
                const offsetX = (logicalW - scaledW) / 2
                const offsetY = (logicalH - scaledH) / 2

                // Draw the scaled image using full source dimensions
                ctx.drawImage(img, 0, 0, sourceW, sourceH, offsetX, offsetY, scaledW, scaledH)
                if (isMounted) setSigned(true)
              } catch (err) {
                console.error('Error in image restoration fallback:', err)
              }
            }
            img.onerror = () => {
              console.error('Error loading image for restoration')
            }
            img.src = savedSigImage
          } else {
            // No scaling needed or no image available - use vector data
            const dataToRestore = shouldScale
              ? scaleData(savedSigData, scaleX, scaleY)
              : savedSigData

            // Clear and restore with scaled data
            const ctx = canvas.getContext('2d')
            if (ctx) {
              ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
              ctx.clearRect(0, 0, targetW, targetH)
            }
            // eslint-disable-next-line no-unused-expressions
            sigRef?.current?.clear()
            // eslint-disable-next-line no-unused-expressions
            sigRef?.current?.fromData(dataToRestore)
            if (isMounted) setSigned(true)
          }
        } catch (err) {
          console.error('Error restoring vector data:', err)
        }
      }

      // Wait for canvas to be ready - use RAF to ensure canvas props have updated
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        const rafId1 = window.requestAnimationFrame(() => {
          if (!isMounted) return
          const rafId2 = window.requestAnimationFrame(restoreVectorData)
          rafIds.push(rafId2)
        })
        rafIds.push(rafId1)
      } else {
        const timeoutId = setTimeout(restoreVectorData, 50)
        timeoutIds.push(timeoutId)
      }
    } else if (savedSigImage) {
      const restoreImg = () => {
        if (!isMounted) return
        try {
          const canvas = getCanvasFromRef(sigRef)
          const ctx = canvas?.getContext && canvas.getContext('2d')
          if (!canvas || !ctx) {
            // Fallback to library method
            if (isMounted) {
              sigRef?.current?.clear()
              sigRef?.current?.fromDataURL(savedSigImage)
              setSigned(true)
            }
            return
          }

          // Get target dimensions from state
          const dpr = getDevicePixelRatioSafe()
          const targetW = sigWidth || (canvas.width / dpr)
          const targetH = sigHeight || (canvas.height / dpr)

          // Ensure canvas is properly sized to match target dimensions
          ensureCanvasSized(canvas, targetW, targetH, true)

          // Clear canvas and reset transform
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
          ctx.clearRect(0, 0, targetW, targetH)

          // Enable high-quality image smoothing
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          // @ts-ignore legacy engines
          if (ctx.msImageSmoothingEnabled !== undefined) {
            ctx.msImageSmoothingEnabled = true
          }

          const img = new Image()
          img.onload = () => {
            if (!isMounted) return
            // ALWAYS scale from the TRUE original canvas size to prevent cumulative scaling
            const originalSize = originalSignatureRef.current.canvasSize || {}
            const originalW = originalSize.width
            const originalH = originalSize.height

            // Use original canvas size if available, otherwise fall back to image dimensions
            const sourceW = originalW || img.naturalWidth || img.width
            const sourceH = originalH || img.naturalHeight || img.height

            if (!sourceW || !sourceH || sourceW === 0 || sourceH === 0) {
              console.error('Invalid image dimensions in fallback:', { sourceW, sourceH, originalSize })
              return
            }

            // Calculate scale factors from TRUE original canvas size to current canvas size
            // This ensures the signature always scales from the original, preventing cumulative scaling
            const scaleX = targetW / sourceW
            const scaleY = targetH / sourceH

            // Use the minimum scale to ensure the entire signature fits without cropping
            // This maintains aspect ratio while scaling according to canvas size
            const uniformScale = Math.min(scaleX, scaleY)
            const scaledW = sourceW * uniformScale
            const scaledH = sourceH * uniformScale

            // Verify scaled dimensions don't exceed canvas
            if (scaledW > targetW || scaledH > targetH) {
              // Recalculate with stricter bounds
              const safeScale = Math.min(targetW / sourceW, targetH / sourceH)
              const safeW = sourceW * safeScale
              const safeH = sourceH * safeScale

              // Center the scaled signature on the canvas
              const offsetX = (targetW - safeW) / 2
              const offsetY = (targetH - safeH) / 2

              // Draw the image scaled proportionally to fit within the canvas without cropping
              ctx.drawImage(img, 0, 0, sourceW, sourceH, offsetX, offsetY, safeW, safeH)
            } else {
              // Center the scaled signature on the canvas
              const offsetX = (targetW - scaledW) / 2
              const offsetY = (targetH - scaledH) / 2

              // Draw the image scaled proportionally to fit within the canvas without cropping
              ctx.drawImage(img, 0, 0, sourceW, sourceH, offsetX, offsetY, scaledW, scaledH)
            }

            if (isMounted) setSigned(true)
          }
          img.onerror = () => {
            console.error('Error loading signature image in fallback restore')
          }
          img.src = savedSigImage
        } catch (err) {
          console.error('Error in fallback restoreImg:', err)
        }
      }
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        const rafId = window.requestAnimationFrame(restoreImg)
        rafIds.push(rafId)
      } else {
        const timeoutId = setTimeout(restoreImg, 0)
        timeoutIds.push(timeoutId)
      }
    }

    // Cleanup function to cancel all async operations
    return () => {
      isMounted = false
      rafIds.forEach(id => {
        if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
          window.cancelAnimationFrame(id)
        }
      })
      timeoutIds.forEach(id => clearTimeout(id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigWidth, sigHeight, forceImageRestore, savedSigImage, savedSigData])

  const openSkipSignatureModal = () => {
    openModal({
      className: 'skip-signature',
      heading: CONSENT_TEXT.skipThisSignatureHeading,
      content: CONSENT_TEXT.skipThisSignatureText,
      confirmButton: CONSENT_TEXT.skip,
      cancelButton: CONSENT_TEXT.return,
      closeOnBackgroundClick: true,
      humi: false,
      onConfirm: () => {
        setSigned(false)
        onSignUpdate('')
        // eslint-disable-next-line no-unused-expressions
        sigRef?.current?.clear()
        resetSignatureSnapshots()
        skipConsentQuestion(questionId)
      }
    })
  }


  // Event handlers (avoid inline lambdas in JSX)
  const handleSwitchToDraw = () => {
    if (isSignatureLocked) return
    setMode(CONSENT_SIGNATURE_MODES.draw)
    setUploadedFile({})
    onSignUpdate('')
    resetSignatureSnapshots()
  }

  const handleSwitchToUpload = () => {
    if (isSignatureLocked) return
    setMode(CONSENT_SIGNATURE_MODES.photoUpload)
    setSigned(false)
    // eslint-disable-next-line no-unused-expressions
    sigRef?.current?.clear()
    onSignUpdate('')
    resetSignatureSnapshots()
  }

  const captureSignatureSnapshot = () => {
    try {
      const canvas = sigRef?.current?.getCanvas ? sigRef.current.getCanvas() : sigRef?.current?._canvas
      if (!canvas || !sigRef?.current) return

      const data = sigRef.current.toData() || []
      const img = sigRef.current.toDataURL() || ''

      // Record logical CSS size (not backing store) so restore can scale correctly
      const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) ? window.devicePixelRatio : 1
      const currentCanvasSize = {
        width: canvas.width / dpr,
        height: canvas.height / dpr
      }

      // CRITICAL: Only save the TRUE original signature once (when first drawn)
      // Never overwrite the original - this prevents cumulative scaling when toggling expand/collapse
      if (!originalSignatureRef.current.data && !originalSignatureRef.current.image) {
        // This is the first time we're saving - store it as the true original
        originalSignatureRef.current = {
          data,
          image: img,
          canvasSize: currentCanvasSize
        }
      } else {
        // Original already exists - don't overwrite it
        // Just update state from the existing original
        console.log('Original signature already exists, preserving it')
      }

      // Update tracking references and state (use original if it exists, otherwise use current)
      prevCanvasSizeRef.current = currentCanvasSize
      setSavedSigData(originalSignatureRef.current.data || data)
      setSavedSigImage(originalSignatureRef.current.image || img)
      setForceImageRestore(!(Array.isArray(originalSignatureRef.current.data || data) && (originalSignatureRef.current.data || data).length))
    } catch (err) {
      console.error('Error capturing signature snapshot:', err)
    }
  }

  const resetSignatureSnapshots = () => {
    setSavedSigData([])
    setSavedSigImage('')
    setForceImageRestore(false)
    originalSignatureRef.current = { data: null, image: null, canvasSize: null }
    prevCanvasSizeRef.current = { width: 0, height: 0 }
  }

  const handleSigEnd = () => {
    const empty = isEmpty(sigRef?.current)
    setSigned(!empty)
    if (empty) {
      onSignUpdate('')
      resetSignatureSnapshots()
      return
    }
    captureSignatureSnapshot()
    onSignUpdate(generateSigData(sigRef?.current))
  }

  const handleSigBegin = () => {
    // User is starting a new stroke. Discard any previously saved "original"
    // so this new signature becomes the new original at onEnd.
    resetSignatureSnapshots()
  }

  const handleClearSignature = () => {
    setSigned(false)
    onSignUpdate('')
    // eslint-disable-next-line no-unused-expressions
    sigRef?.current?.clear()
    resetSignatureSnapshots()
  }

  const handleToggleExpand = () => {
    // Don't capture a new snapshot here - it would overwrite the true original
    // The resize effect will handle preserving the signature when dimensions change
    // We just need to update the previous canvas size reference for the current state
    try {
      const canvas = sigRef?.current?.getCanvas ? sigRef.current.getCanvas() : sigRef?.current?._canvas
      if (canvas) {
        const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) ? window.devicePixelRatio : 1
        // Update prevCanvasSizeRef to track current size before toggle
        // This helps the restore effect know what size we're scaling from
        prevCanvasSizeRef.current = {
          width: canvas.width / dpr,
          height: canvas.height / dpr
        }
      }
    } catch (_) {
      // Fallback to state values if we can't get canvas
      prevCanvasSizeRef.current = { width: sigWidth, height: sigHeight }
    }

    setIsExpanded(prev => {
      const next = !prev
      // Remember that the user manually changed this so auto-expand won't fight them
      expandPreferenceRef.current = next ? EXPANDED : COLLAPSED
      // If we are collapsing and have signature data, briefly show a preserved hint
      if (!next && ((savedSigData && savedSigData.length) || savedSigImage)) {
        setShowPersistedNotice(true)
        // Clear any existing timeout
        if (persistedNoticeTimeoutRef.current) {
          clearTimeout(persistedNoticeTimeoutRef.current)
        }
        persistedNoticeTimeoutRef.current = setTimeout(() => setShowPersistedNotice(false), 2500)
      }
      return next
    })
    // Size will recompute via resize effect (listens to isExpanded)
    // The resize effect will preserve the original signature and trigger restore
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (persistedNoticeTimeoutRef.current) {
        clearTimeout(persistedNoticeTimeoutRef.current)
      }
    }
  }, [])

  // Footer text computation per acceptance criteria
  const participantId = (() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.user)
      if (!raw) return undefined
      const parsed = JSON.parse(raw)
      return parsed?.id
    } catch (_) {
      return undefined
    }
  })()
  const formattedDate = moment().format('MMMM D, YYYY')
  const footerText = (() => {
    if (readOnly) return ''
    if (isCaregiverSignature) return `${formattedDate}`
    if (fullName && fullName.trim().length > 0) return `${fullName} | ${formattedDate}`
    if (participantId) return `${participantId} | ${formattedDate}`
    return `${formattedDate}`
  })()

  // Using asset icons for expand/collapse


  return (
    <div className='consent-section signature'>
      <div className='divider' />
      {
        !readOnly && (
          <p>{strings.consent_agree_terms}</p>
        )
      }
      <div className={`signing-box ${isExpanded ? 'expanded' : ''}`}>
        {/* Mode switcher moved inside signing-box to position relative to it */}
        {showToggle && !readOnly ? (
          <div className='mode-switcher'>
            <div className='switcher-container'>
              {/** Disable switching if any signature is already provided (drawn or uploaded) */}
              {/** Using local component state: signed OR uploadedFile */}
              <button
                className={`${mode === CONSENT_SIGNATURE_MODES.draw ? 'active' : ''} ${isSignatureLocked ? 'disabled' : ''}`}
                disabled={isSignatureLocked}
                onClick={handleSwitchToDraw}
              >
                <span className='mode-icon'>
                  <img src={unionIcon} alt={CONSENT_ALT_TEXT.draw} width={20} height={20} />
                </span>
              </button>
              <button
                className={`${mode === CONSENT_SIGNATURE_MODES.photoUpload ? 'active' : ''} ${isSignatureLocked ? 'disabled' : ''}`}
                disabled={isSignatureLocked}
                onClick={handleSwitchToUpload}
              >
                <span className='mode-icon'>
                  <img src={cameraIcon} alt={CONSENT_ALT_TEXT.camera} width={20} height={20} />
                </span>
              </button>
            </div>
          </div>
        ) : null}
        {mode === CONSENT_SIGNATURE_MODES.draw ? (
          <>
            <div
              className={`signing-area ${isExpanded ? 'expanded' : ''}`}
              ref={signingAreaRef}
            >
              {!readOnly && (
                <div className='signing-controls'>
                  {showPersistedNotice ? (
                    <span className='signature-preserved-hint'>Signature preserved</span>
                  ) : null}
                  {signed && (
                    <button
                      type='button'
                      className='clear-button'
                      onClick={handleClearSignature}
                    >
                      <span>{strings.clear}</span>
                      <img src={closeSvg} alt='' />
                    </button>
                  )}
                  <button
                    type='button'
                    className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                    onClick={handleToggleExpand}
                    aria-label={isExpanded ? strings?.collapse || COLLAPSED : strings?.expand || EXPANDED}
                  >
                    <img src={!isExpanded ? resizeMinIcon : resizeMaxIcon} alt='' />
                  </button>
                </div>
              )}
              <SigCanvas
                penColor='black'
                clearOnResize={false}
                canvasProps={{
                  width: sigWidth || 512,
                  height: sigHeight || 256,
                  className: 'sigCanvas'
                }}
                ref={sigRef}
                onBegin={handleSigBegin}
                onEnd={handleSigEnd}
              />
              <div className='signature-baseline' />
            </div>
            {!readOnly ? (
              <>
                <p className='name-date'>{footerText}</p>
                {canSkip ? (
                  <Button
                    link
                    className='skip-signature-link'
                    content={CONSENT_TEXT.skipSignature}
                    onClick={openSkipSignatureModal}
                  />
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <SignatureUpload
            readOnly={readOnly}
            onSignUpdate={onSignUpdate}
            openModal={openModal}
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            consentName={consentName}
            pdfUrl={pdfUrl}
            printToSign={printToSign}
            promptText={promptText}
            consentId={consentId}
            consentVersion={consentVersion}
            dispatch={dispatch}
          />
        )}
      </div>
      {!isFinalSection && <div className='divider' />}
    </div >
  )
}

export default Signature

Signature.propTypes = {
  fullName: PropTypes.string,
  isFinalSection: PropTypes.bool,
  onSignUpdate: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  signature: PropTypes.string,
  readOnly: PropTypes.bool,
  defaultSignaturePadRatio: PropTypes.number,
  promptText: PropTypes.string,
  canSkip: PropTypes.bool,
  questionId: PropTypes.string,
  openModal: PropTypes.func,
  skipConsentQuestion: PropTypes.func,
  signatureOptions: PropTypes.arrayOf(PropTypes.string),
  showToggle: PropTypes.bool,
  consentName: PropTypes.string,
  consentVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pdfUrl: PropTypes.string,
  printToSign: PropTypes.bool,
  consentId: PropTypes.string,
  dispatch: PropTypes.func,
  isCaregiverSignature: PropTypes.bool
}